import Vue from "vue";
import { resolveLocation } from "@/utils/omsPickList";
import { siteBadge } from "@/utils/locationSites";

// Helpers for the Messages inbox and the Order Queue badge.

/**
 * { conversationId: lastMessageAt when a send's delivery became uncertain }.
 * Shared (not per component) on purpose: leaving a thread and coming back
 * recreates the component, and the uncertainty must survive that. It lifts by
 * itself the moment the conversation's lastMessageAt moves (the pull stored
 * what eBay accepted), or when staff choose "Send anyway".
 */
const uncertain = Vue.observable({ byId: {} });
// `byId` is REPLACED on every change (Vue 2 cannot see a key being added to an
// observed object, so a computed that read a missing key would never re-run).

export function markUncertain(conversationId, lastMessageAt) {
  uncertain.byId = { ...uncertain.byId, [conversationId]: lastMessageAt };
}

export function clearUncertain(conversationId) {
  const rest = { ...uncertain.byId };
  delete rest[conversationId];
  uncertain.byId = rest;
}

/** True while the conversation still has the lastMessageAt it had when the send went uncertain. */
export function isUncertain(conversationId, lastMessageAt) {
  return uncertain.byId[conversationId] !== undefined && uncertain.byId[conversationId] === lastMessageAt;
}

/** Tests only. */
export function resetUncertain() {
  uncertain.byId = {};
}

/**
 * { conversationId: { key, text } }: the idempotency key (and the text it was
 * made for) of a reply that has not been confirmed yet. Depop deduplicates on
 * it, so "Send anyway" after an uncertain send MUST reuse the key, and a
 * different text must get a new one. Replaced, not mutated, like `uncertain`.
 */
const pending = Vue.observable({ byId: {} });

/** A lowercase UUIDv4. crypto.randomUUID where there is one. */
export function newClientMessageId() {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** The key for sending `text` on this conversation: the pending one when the text is unchanged, else a fresh one. */
export function pendingKeyFor(conversationId, text) {
  const have = pending.byId[conversationId];
  if (have && have.text === text) return have.key;
  const key = newClientMessageId();
  pending.byId = { ...pending.byId, [conversationId]: { key, text } };
  return key;
}

export function clearPendingKey(conversationId) {
  const rest = { ...pending.byId };
  delete rest[conversationId];
  pending.byId = rest;
}

/** Tests only. */
export function resetPending() {
  pending.byId = {};
}

// Never below 15: the Order Queue defaults to 15 days, so a link must not narrow it.
const RANGES = [15, 30, 90];

/** The smallest Order Queue "Sold within" range (15 or wider) that still covers placedAt, else 90. */
export function withinFor(placedAt, now = Date.now()) {
  const t = placedAt ? Date.parse(placedAt) : NaN;
  if (Number.isNaN(t)) return 90;
  const days = Math.max(0, (now - t) / 86400000);
  return RANGES.find((r) => days <= r) || 90;
}

/** Where the "Order <id>" link goes: the Order Queue, filtered to that order. Only a conversation LINKED to a Sellary order has one. */
export function orderQueueHref(c, now = Date.now()) {
  if (!c || !c.externalOrderId || !c.orderId) return null;
  return { path: "/orders", query: { order: c.externalOrderId, within: String(withinFor(c.orderPlacedAt, now)) } };
}

export function inventoryHref(sku) {
  return sku ? { path: "/inventory", query: { sku } } : null;
}

/** "View on eBay": the order, else the item, else the eBay inbox. */
export function ebayHref(c) {
  if (c && c.externalOrderId) return "https://www.ebay.com/sh/ord/details?orderid=" + encodeURIComponent(c.externalOrderId);
  if (c && c.externalItemRef) return "https://www.ebay.com/itm/" + encodeURIComponent(c.externalItemRef);
  return "https://mesg.ebay.com/mesgweb/ViewMessages/0";
}

const PLATFORMS = { ebay: "eBay", poshmark: "Poshmark", depop: "Depop" };

/** "eBay" / "Poshmark" / "Depop" for a Conversation.channel. */
export function platformLabel(channel) {
  return PLATFORMS[channel] || String(channel || "");
}

/** What to call a buyer whose username we do not have. */
export function memberLabel(channel) {
  return `${platformLabel(channel) || "Marketplace"} member`;
}

// OPT-82: a Poshmark listing's public comment thread, one per listing.
export const COMMENT_MAX = 500;

export function isListingComments(c) {
  return !!c && c.kind === "listing_comments";
}

/** What the list row and thread header call a conversation: comments have many commenters, so no buyer name. */
export function conversationName(c) {
  if (isListingComments(c)) return "Comments";
  return (c && c.otherPartyUsername) || memberLabel(c && c.channel);
}

/**
 * "@name " for the newest inbound comment's sender, else the conversation's otherPartyUsername, else "".
 * Our own comments are never the newest inbound, so a reply never mentions us.
 */
export function mentionPrefill(messages, c) {
  let best = null;
  for (const m of messages || []) {
    if (m && m.direction === "inbound" && m.senderUsername && (!best || Date.parse(m.sentAt) >= Date.parse(best.sentAt))) best = m;
  }
  const u = (best && best.senderUsername) || (c && c.otherPartyUsername) || "";
  return u ? "@" + u + " " : "";
}

/** "View on <platform>": the marketplace's own page for this conversation. */
export function platformHref(c) {
  if (isListingComments(c) && c.channel === "poshmark" && c.externalItemRef) {
    return "https://poshmark.com/listing/" + encodeURIComponent(c.externalItemRef);
  }
  if (c && c.channel === "poshmark") {
    return c.externalOrderId
      ? "https://poshmark.com/order/sales/" + encodeURIComponent(c.externalOrderId)
      : "https://poshmark.com/order/sales";
  }
  // Depop has no public URL for a conversation, so its inbox.
  if (c && c.channel === "depop") return "https://www.depop.com/messages/";
  return ebayHref(c);
}

// Failures where the marketplace definitely did NOT take the message. Anything
// else (a timeout, a dropped connection, an unlabelled gateway error) may have
// reached it, so it blocks an immediate resend.
export const DEFINITE_FAILURES = [
  "EBAY_REJECTED",
  "POSHMARK_REJECTED",
  "POSHMARK_UNREACHABLE", // OPT-82: the listing could not be read before posting, so nothing was sent
  "BAD_REQUEST",
  "NOT_FOUND",
  "RECONNECT_REQUIRED",
  "RELOGIN_REQUIRED",
  "REPLIES_CLOSED",
  "UNAUTHENTICATED",
  // Depop (OPT-69): each is certain not to have sent.
  "REPLIES_UNAVAILABLE",
  "PHONE_VERIFICATION_REQUIRED",
  "SESSION_EXPIRED",
  "DEPOP_REJECTED",
];

export const REPLIES_CLOSED_TEXT = "Replies are closed on Poshmark for this order.";
export const DEPOP_REPLIES_CLOSED_TEXT = "Replies are closed on Depop for this conversation.";

/** The "replies are closed" sentence for a conversation's marketplace. */
export function repliesClosedText(channel) {
  return channel === "depop" ? DEPOP_REPLIES_CLOSED_TEXT : REPLIES_CLOSED_TEXT;
}

/**
 * What to tell staff when a send fails. `definite` = nothing was sent, so they
 * may try again; otherwise the caller marks the send uncertain.
 */
export function sendFailure(code, message, channel) {
  const definite = DEFINITE_FAILURES.includes(code);
  if (!definite) return { definite: false, text: null };
  const text = {
    RECONNECT_REQUIRED: "This eBay account needs reconnecting before it can send. Reconnect it on the Accounts page.",
    RELOGIN_REQUIRED: "This Poshmark account's session has ended. Sign in to Poshmark in Chrome (the profile with Sellary Session Sync); Session Sync picks it up.",
    EBAY_REJECTED: `eBay did not accept the reply: ${message}`,
    POSHMARK_REJECTED: `Poshmark did not accept the reply: ${message}`,
    REPLIES_CLOSED: repliesClosedText(channel),
  }[code];
  return { definite: true, text: text || message };
}

/** { [orderId]: conversationId } for unread conversations. The newest wins. */
export function unreadByOrderId(conversations) {
  const best = {};
  for (const c of conversations || []) {
    if (!c || !c.orderId || c.readState !== "unread") continue;
    const have = best[c.orderId];
    if (!have || Date.parse(c.lastMessageAt) > Date.parse(have.lastMessageAt)) best[c.orderId] = c;
  }
  return Object.fromEntries(Object.entries(best).map(([orderId, c]) => [orderId, c.id]));
}

/** Only https URLs become links: buyer content is untrusted. */
export function safeMediaUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "https:" ? u.toString() : null;
  } catch (_) {
    return null;
  }
}

export function parseMedia(media) {
  if (!media) return [];
  try {
    const m = typeof media === "string" ? JSON.parse(media) : media;
    return Array.isArray(m) ? m : [];
  } catch (_) {
    return [];
  }
}

export function relativeTime(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(t).toLocaleDateString();
}

// ---- OPT-81: the item card -------------------------------------------------

/** True when the conversation is tied to an order or a listing, so a card is worth showing. */
export function hasLinkedItem(c) {
  return !!(c && (isListingComments(c) || c.orderId || c.externalOrderId || c.listingId || c.listingSku));
}

/** Changes exactly when what the card resolves from changes (not on every poll). */
export function itemResolutionKey(c) {
  return c ? [c.id, c.orderId, c.externalOrderId, c.listingId, c.listingSku].map((v) => v || "").join("|") : "";
}

// The SKU as stored (trimmed, OPT-76): the card links by it, and the inventory lookup is byte-exact.
const trimmedSku = (sku) => (typeof sku === "string" && sku.trim()) || null;

/** The first usable image URL. `images` is [String]: the array and its entries can be null. */
export function firstImage(inv) {
  const imgs = inv && Array.isArray(inv.images) ? inv.images : [];
  return imgs.find((u) => typeof u === "string" && u) || null;
}

/** What the conversation already holds about the item: the card's row when nothing resolves. */
export function fallbackItemRow(c, over = {}) {
  return {
    key: "fallback",
    title: (c && (c.listingTitle || c.title)) || "Item",
    sku: trimmedSku(c && c.listingSku),
    image: null,
    quantity: null,
    found: false,
    location: null,
    locationSource: null,
    ...over,
  };
}

/**
 * The inventory rows a SKU lookup returned -> { item, found }. SKUs are not
 * unique and quarantined rows are status "Duplicate". One live row is the item;
 * several mean the SKU exists but which one is a guess, so there is no item
 * (no photo, no title) yet the SKU still links to the inventory search.
 */
export function resolveSkuRows(rows) {
  const live = (rows || []).filter((r) => r && r.status !== "Duplicate");
  if (live.length === 1) return { item: live[0], found: true, candidates: live };
  return { item: null, found: live.length > 1, candidates: live };
}

/**
 * Where the card says an item is -> { location, locationSource }, by the Pick
 * List's rule (location field, else read from the SKU). An unfound row has no
 * location. An ambiguous SKU (found, no single item) shows one only when every
 * candidate agrees: we cannot say which row is the item.
 */
export function rowLocation({ found, item, candidates, sku }) {
  const none = { location: null, locationSource: null };
  if (!found) return none;
  const line = { sku };
  if (item) {
    const r = resolveLocation(item, line);
    return { location: r.location, locationSource: r.source };
  }
  const all = (candidates || []).map((cand) => resolveLocation(cand, line));
  if (!all.length || !all[0].location || all.some((r) => r.location !== all[0].location)) return none;
  return { location: all[0].location, locationSource: all.some((r) => r.source === "field") ? "field" : "sku" };
}

/** One card row from an order line and the inventory item it resolved to (or null). */
export function itemRowFrom(line, inv, c, { found = !!inv, candidates } = {}) {
  const fb = (c && (c.listingTitle || c.title)) || "Item";
  const sku = (inv && inv.sku) || trimmedSku(line.sku);
  return {
    key: line.id,
    title: (inv && inv.title) || line.titleSnapshot || fb,
    sku,
    image: firstImage(inv),
    quantity: line.quantity || null,
    found,
    ...rowLocation({ found, item: inv, candidates, sku }),
  };
}

// ---- OPT-294: location badges on the inbox row -------------------------------

/** The item rows fetchConversationItems returned -> distinct badges from the tenant's sites (OPT-295), store first, then by label. */
export function locationBadges(rows, setup) {
  const seen = new Map();
  for (const r of rows || []) {
    const b = siteBadge(r && r.location, setup);
    if (b) seen.set(`${b.kind}|${b.label}`, b);
  }
  return [...seen.values()].sort((a, b) => (a.kind === b.kind ? a.label.localeCompare(b.label) : a.kind === "store" ? -1 : 1));
}
